import { useLocation } from "react-router-dom";

import { useFlags } from "launchdarkly-react-client-sdk";
import { skipToken } from "@reduxjs/toolkit/query";
import { faBolt, faBookOpen, faCrystalBall } from "@fortawesome/pro-regular-svg-icons";

import type { AccountDTO } from "../../common/types/AccountDTO";
import type { UserDTO } from "../../common/types/UserDTO";
import { useGetConfigQuery } from "../store/services/config";
import { useGetModulesQuery } from "../store/services/modules";
import { constructLinks, getActiveLink } from "../utils/links";
import type { NavigationLink } from "../types";
import type { FeatureFlagState } from "./useLaunchDarkly";

export const useModules = (user?: UserDTO, account?: AccountDTO) => {
  const { data: config } = useGetConfigQuery();
  const rootPath = config?.rootPath;
  const { pathname } = useLocation();
  const allModuleAccess = account?.properties?.allModuleAccess;
  const accountId = account?.id;
  const flags = useFlags<FeatureFlagState>();

  const {
    data: modules,
    isLoading: isLoadingModules,
    isError: isModulesError,
  } = useGetModulesQuery(user ? { userId: user.id } : skipToken);

  const moduleLinks = constructLinks(modules, user, accountId, allModuleAccess, rootPath);

  // Knowledge isn't a server-side module — it's a client-side surface
  // gated by the same feature flag that gates the KnowledgePage itself.
  // Slot it directly after Ask so the top cluster reads Ask · Knowledge.
  // Points to the existing in-Ask route; no new top-level route is added.
  const links: NavigationLink[] = [...moduleLinks];
  if (flags.showRexFileUpload && rootPath) {
    const askIndex = links.findIndex((link) => link.title === "Ask");
    if (askIndex !== -1) {
      links.splice(askIndex + 1, 0, {
        to: `${rootPath}/ask/rex/knowledge`,
        title: "Knowledge",
        fullTitle: "Knowledge",
        icon: faBookOpen,
      });
    }
  }

  // Same client-side surface pattern as Knowledge; slot after the Ask cluster
  // (after Knowledge when present) so it reads Ask · Knowledge · Forecasts.
  if (flags.showForecasts && rootPath) {
    const anchorTitle = links.some((link) => link.title === "Knowledge")
      ? "Knowledge"
      : "Ask";
    const anchorIndex = links.findIndex((link) => link.title === anchorTitle);
    if (anchorIndex !== -1) {
      links.splice(anchorIndex + 1, 0, {
        to: `${rootPath}/ask/rex/forecasts`,
        title: "Forecasts",
        fullTitle: "Forecasts",
        icon: faCrystalBall,
      });
    }
  }

  // Same client-side surface pattern as Knowledge/Forecasts; slot into the Ask
  // cluster after Forecasts so it reads Ask · Knowledge · Forecasts · Automations.
  // Nested under Ask (/ask/rex/automations), rendered in the Ask panel layout.
  if (flags.showWorkflows && rootPath) {
    const anchorTitle = ["Forecasts", "Knowledge", "Ask"].find((title) =>
      links.some((link) => link.title === title),
    );
    const anchorIndex = anchorTitle
      ? links.findIndex((link) => link.title === anchorTitle)
      : -1;
    if (anchorIndex !== -1) {
      links.splice(anchorIndex + 1, 0, {
        to: `${rootPath}/ask/rex/automations`,
        title: "Automations",
        fullTitle: "Automations",
        icon: faBolt,
      });
    }
  }

  const activeLink = getActiveLink(links, pathname, rootPath);

  return {
    links,
    activeLink,
    hasLinks: !isLoadingModules && !!modules && !!user,
    isModulesError,
  };
};
